import type { FieldSpec } from "../templates/types";
import type { BooleanWithNotesValue, FieldValue, Locale } from "./types";

export type NormalizeResult = {
  value: FieldValue;
  /** Set when the source text implied a range or lower bound rather than an exact count (e.g. "3-4", "a few"). validate.ts does not clamp on this — it is informational for provenance/UI, not a reason to change the value. */
  approximate: boolean;
} | null; // null = unmappable; validate.ts turns this into `needs_confirmation`, never a silent default.

const WORD_NUMBERS: Record<Locale, Record<string, number>> = {
  en: {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
    twenty: 20,
    thirty: 30,
    forty: 40,
    fifty: 50,
    sixty: 60,
    seventy: 70,
  },
  es: {
    cero: 0,
    uno: 1,
    una: 1,
    dos: 2,
    tres: 3,
    cuatro: 4,
    cinco: 5,
    seis: 6,
    siete: 7,
    ocho: 8,
    nueve: 9,
    diez: 10,
    once: 11,
    doce: 12,
    trece: 13,
    catorce: 14,
    quince: 15,
    dieciseis: 16,
    diecisiete: 17,
    dieciocho: 18,
    diecinueve: 19,
    veinte: 20,
    treinta: 30,
    cuarenta: 40,
    cincuenta: 50,
    sesenta: 60,
    setenta: 70,
  },
};

// Idiomatic phrases checked before generic word/number parsing. Order matters:
// more specific phrases first so "every day" isn't accidentally caught by a
// looser rule later.
const IDIOMS: Record<Locale, Array<{ pattern: RegExp; value: number; approximate: boolean }>> = {
  en: [
    { pattern: /\bnone\b|\bnever\b|\bzero\b|\bnot at all\b/i, value: 0, approximate: false },
    { pattern: /\bevery ?day\b|\ball days\b|\bdaily\b/i, value: 7, approximate: false },
    { pattern: /\ba couple\b/i, value: 2, approximate: true },
    { pattern: /\ba few\b/i, value: 3, approximate: true },
  ],
  es: [
    { pattern: /\bninguno\b|\bninguna\b|\bnunca\b|\bnada\b/i, value: 0, approximate: false },
    { pattern: /\btodos los d[ií]as\b|\bcada d[ií]a\b|\bdiariamente\b/i, value: 7, approximate: false },
    { pattern: /\bun par\b/i, value: 2, approximate: true },
    { pattern: /\bunos? pocos\b|\bunas? pocas\b/i, value: 3, approximate: true },
  ],
};

/**
 * Lowercases and strips combining accents for MATCHING PURPOSES ONLY. Never
 * write the result back to state — merge.ts's quote-presence check and this
 * module's own enum/idiom matching both use it as a throwaway comparison
 * copy; the participant's original text is always what gets stored (§8).
 */
export function normalizeForMatching(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, ""); // strip combining accents for matching only — never written back (§8)
}

/** "3-4", "3 to 4", "3 a 4" → 4 (round up = the higher bound), marked approximate. */
function matchRange(text: string): number | null {
  const match = text.match(/(\d+)\s*(?:-|to|a)\s*(\d+)/);
  if (!match) return null;
  const hi = Math.max(Number(match[1]), Number(match[2]));
  return Number.isFinite(hi) ? hi : null;
}

export function normalizeInteger(rawValue: string, locale: Locale): NormalizeResult {
  const text = normalizeForMatching(rawValue);
  if (!text) return null;

  for (const idiom of IDIOMS[locale]) {
    if (idiom.pattern.test(text)) {
      return { value: idiom.value, approximate: idiom.approximate };
    }
  }

  const range = matchRange(text);
  if (range !== null) {
    return { value: range, approximate: true };
  }

  const digitMatch = text.match(/-?\d+(\.\d+)?/);
  if (digitMatch) {
    const n = Number(digitMatch[0]);
    if (Number.isFinite(n)) {
      return { value: Math.round(n), approximate: !Number.isInteger(n) };
    }
  }

  const words = text.split(/[\s-]+/);
  for (const word of words) {
    const n = WORD_NUMBERS[locale][word];
    if (n !== undefined) {
      return { value: n, approximate: false };
    }
  }

  return null;
}

/** Matches raw text against the field's template-declared synonym map ONLY. No fuzzy matching, no embeddings, no silent guessing (§8) — an unmapped value is `null`, which validate.ts turns into `needs_confirmation`. */
export function normalizeEnum(rawValue: string, field: FieldSpec): NormalizeResult {
  const text = normalizeForMatching(rawValue);
  if (!text) return null;

  const options = field.options ?? [];
  for (const option of options) {
    if (normalizeForMatching(option) === text) {
      return { value: option, approximate: false };
    }
  }

  const synonyms = field.synonyms ?? {};
  for (const option of options) {
    const phrases = synonyms[option] ?? [];
    for (const phrase of phrases) {
      if (normalizeForMatching(phrase) === text) {
        return { value: option, approximate: false };
      }
    }
  }

  return null;
}

const AFFIRMATIVE: Record<Locale, RegExp> = {
  en: /\b(yes|yeah|yep|sometimes|often|frequently|has happened|worried)\b/i,
  es: /\b(s[ií]|a veces|seguido|frecuentemente|ha pasado|preocup[oó])\b/i,
};

const NEGATIVE: Record<Locale, RegExp> = {
  en: /\b(no|never|not really|not at all)\b/i,
  es: /\b(no|nunca|para nada)\b/i,
};

export function normalizeBooleanWithNotes(rawValue: string, locale: Locale): NormalizeResult {
  const text = rawValue.trim();
  if (!text) return null;

  const normalized = normalizeForMatching(text);
  let flag: boolean | null = null;
  if (NEGATIVE[locale].test(normalized)) flag = false;
  else if (AFFIRMATIVE[locale].test(normalized)) flag = true;

  if (flag === null) return null;

  const value: BooleanWithNotesValue = { flag, notes: text };
  return { value, approximate: false };
}

/** Splits a raw phrase into one or more barrier strings. Structured (a deterministic function of the quote), so splitting/trimming is fine — this is not "verbatim" content, which is never coerced (§8). */
export function normalizeStringArray(rawValue: string): NormalizeResult {
  const text = rawValue.trim();
  if (!text) return null;

  const items = text
    .split(/,|;|\by\b|\band\b/i)
    .map((s) => s.trim())
    .filter(Boolean);

  return items.length > 0 ? { value: items, approximate: false } : null;
}

/** Dispatches by field type. `verbatim` fields are never coerced — this function is not called for them; the reducer appends the raw quote directly to the append-only notes array (§8). */
export function normalize(rawValue: string, field: FieldSpec, locale: Locale): NormalizeResult {
  switch (field.type) {
    case "integer":
      return normalizeInteger(rawValue, locale);
    case "enum":
      return normalizeEnum(rawValue, field);
    case "boolean_with_notes":
      return normalizeBooleanWithNotes(rawValue, locale);
    case "string_array":
      return normalizeStringArray(rawValue);
    case "verbatim":
      return null;
    default: {
      const exhaustive: never = field.type;
      throw new Error(`normalize: unhandled field type ${String(exhaustive)}`);
    }
  }
}
